import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, AlertCircle, CheckCircle, Paperclip } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { isValidEmail, readFunctionError } from '../../../lib/email';
import { errorMessage } from '../../../lib/errors';

interface EmailQuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerEmail: string;
    customerName: string;
    quoteNumber: string;
    quoteId: string;
    total: number;
    businessName: string;
    generatePdfBase64: () => Promise<string | null>;
    onSuccess: () => void;
    businessEmail?: string;
    vrm?: string;
    vehicleName?: string;
}

/**
 * Quote email composer — the quote sibling of EmailInvoiceModal. Generates the
 * quote PDF client-side and posts it to the authenticated `send-quote-email`
 * edge function (which attaches it and emails the customer with Approve/Decline
 * links). Deliberately routes through send-quote-email, NOT send-email: the
 * latter is locked to service-role callers and would 401 a browser call.
 */
export default function EmailQuoteModal({
    isOpen,
    onClose,
    customerEmail,
    customerName,
    quoteNumber,
    quoteId,
    total,
    businessName,
    generatePdfBase64,
    onSuccess,
    businessEmail,
    vrm,
    vehicleName,
}: EmailQuoteModalProps) {
    const [emailTo, setEmailTo] = useState(customerEmail);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sendCopy, setSendCopy] = useState(true);
    const [status, setStatus] = useState<'idle' | 'generating' | 'sending' | 'success'>('idle');
    const [error, setError] = useState<string | null>(null);

    // Reset/populate the form each time the modal opens.
    useEffect(() => {
        if (isOpen) {
            setEmailTo(customerEmail);
            const vehicleText = vrm ? ` for your ${vehicleName || 'vehicle'} (${vrm})` : '';
            setSubject(`Quote ${quoteNumber} from ${businessName}${vrm ? ` - ${vrm}` : ''}`);
            setMessage(`Hi ${customerName},\n\nThank you for your enquiry. Please find your quote${vehicleText} attached for £${Number(total).toFixed(2)}.\n\nUse the Approve or Decline buttons in this email to let us know how you'd like to proceed. This quote is valid for 30 days.\n\nBest regards,\n${businessName}`);
            setStatus('idle');
            setError(null);
        }
    }, [isOpen, customerEmail, quoteNumber, businessName, customerName, total, vrm, vehicleName]);

    const handleClose = () => {
        if (status !== 'idle' && status !== 'success') return;
        setError(null);
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!emailTo.trim()) {
            setError('Recipient email is required');
            return;
        }
        if (!isValidEmail(emailTo)) {
            setError('That email address looks invalid — check it includes a domain like "name@example.com".');
            return;
        }

        try {
            setStatus('generating');

            const pdfBase64 = await generatePdfBase64();
            if (!pdfBase64) {
                throw new Error('Could not generate the quote PDF attachment.');
            }

            setStatus('sending');

            const { data, error: fnError } = await supabase.functions.invoke('send-quote-email', {
                body: {
                    quote_id: quoteId,
                    app_url: window.location.origin,
                    email: emailTo.trim(),
                    subject: subject.trim(),
                    message: message.trim(),
                    pdf_base64: pdfBase64,
                    send_copy: sendCopy,
                    business_email: businessEmail,
                },
            });

            if (fnError) throw await readFunctionError(fnError, 'Failed to send quote');
            if (data?.error) throw new Error(data.error);

            setStatus('success');
            setTimeout(() => onSuccess(), 1500);
        } catch (err) {
            console.error('Quote email error:', err);
            setError(errorMessage(err) || 'Failed to send quote');
            setStatus('idle');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg bg-panel border border-line rounded-xl shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-tint/10">
                    <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                        <Send className="w-5 h-5 text-accent" />
                        Email Quote
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-tint/10 transition-colors text-fg-3 hover:text-fg"
                        disabled={status !== 'idle' && status !== 'success'}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* To */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            To <span className="text-danger">*</span>
                        </label>
                        <input
                            type="email"
                            required
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50"
                        />
                    </div>

                    {/* Subject */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            Subject <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50"
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Message</label>
                        <textarea
                            rows={6}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none resize-none disabled:opacity-50"
                        />
                    </div>

                    {/* Attachment preview */}
                    <div className="flex items-center gap-3 p-3 border border-tint/10 rounded-lg bg-tint/5">
                        <div className="w-10 h-10 bg-danger/20 rounded flex items-center justify-center">
                            <span className="text-danger font-bold text-[10px]">PDF</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-fg truncate">Quote_{quoteNumber}.pdf</p>
                            <p className="text-xs text-fg-4 flex items-center gap-1">
                                <Paperclip className="w-3 h-3" /> Attached automatically
                            </p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-success" />
                    </div>

                    {/* Send copy */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="sendQuoteCopy"
                            checked={sendCopy}
                            onChange={(e) => setSendCopy(e.target.checked)}
                            disabled={status !== 'idle'}
                            className="rounded border-tint/10 bg-tint/5 text-accent focus:ring-accent disabled:opacity-50"
                        />
                        <label htmlFor="sendQuoteCopy" className="text-sm text-fg-3 select-none cursor-pointer">
                            Send a copy to my email {businessEmail && `(${businessEmail})`}
                        </label>
                    </div>

                    {/* Error */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Actions */}
                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 px-4 py-2.5 rounded-lg font-bold text-fg-2 hover:bg-tint/5 border border-tint/10 transition-colors disabled:opacity-50"
                            disabled={status !== 'idle' && status !== 'success'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={status !== 'idle'}
                            className={`flex-1 font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 min-w-[120px] ${status === 'success' ? 'bg-success-fill text-on-fill' : 'fd-btn-primary'}`}
                        >
                            {status === 'idle' && (<><Send className="w-5 h-5" />Send Quote</>)}
                            {status === 'generating' && (<><Loader2 className="w-5 h-5 animate-spin" />Generating PDF...</>)}
                            {status === 'sending' && (<><Loader2 className="w-5 h-5 animate-spin" />Sending...</>)}
                            {status === 'success' && (<><CheckCircle className="w-5 h-5" />Sent!</>)}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}
